import React, { useState } from 'react';
import { AgentLayout } from '../../components/layout/AgentLayout';
import { Card } from '../../components/common/Card';
import { Spinner } from '../../components/common/Spinner';
import { useAgentDashboard } from '../../hooks/useAgent';
import { useWallet } from '../../hooks/useWallet';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../utils/formatters';
import { Input } from '../../components/common/Input';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { ShoppingCart, TrendingUp, Wallet, Award } from 'lucide-react';

export const AgentDashboard: React.FC = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { data, isLoading } = useAgentDashboard({ startDate: startDate || undefined, endDate: endDate || undefined });
  const { user } = useAuth();
  const { data: walletData } = useWallet();

  if (isLoading) return <AgentLayout><Spinner size="lg" /></AgentLayout>;

  const d = data?.data;
  const currency = walletData?.data?.currency || user?.currency || 'USD';

  return (
    <AgentLayout>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Agent Dashboard</h1>
          <Badge variant={d?.agentStatus === 'APPROVED' ? 'success' : d?.agentStatus === 'DISABLED' ? 'danger' : 'warning'}>{d?.agentStatus}</Badge>
          {d?.canPayoutLargeWins && <Badge variant="info">Super Agent</Badge>}
        </div>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          {d?.businessName ? `${d.businessName} · ` : ''}Your sales performance and metrics
        </p>
      </div>

      <Card className="mb-6">
        <div className="flex items-end gap-4">
          <Input label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <Input label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          {(startDate || endDate) && <Button variant="outline" onClick={() => { setStartDate(''); setEndDate(''); }}>Reset</Button>}
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { icon: Wallet, label: 'Wallet Balance', value: formatCurrency(d?.walletBalance || 0, currency), color: 'text-indigo-600', bg: 'bg-indigo-50 dark:bg-indigo-950' },
          { icon: ShoppingCart, label: 'Tickets Sold', value: String(d?.totalTicketsSold || 0), color: 'text-purple-600', bg: 'bg-purple-50 dark:bg-purple-950' },
          { icon: TrendingUp, label: 'Commission Earned', value: formatCurrency(d?.totalCommissionEarned || 0, currency), color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-950' },
          { icon: Award, label: 'Payouts Processed', value: String(d?.totalPayoutsProcessed || 0), color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-950' },
        ].map(({ icon: Icon, label, value, color, bg }) => (
          <Card key={label}>
            <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center mb-3`}>
              <Icon className={`h-5 w-5 ${color}`} />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-0.5">{value}</p>
          </Card>
        ))}
      </div>

      {d?.recentSales && d.recentSales.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Recent Sales</h3>
          <div className="space-y-2">
            {d.recentSales.map((sale: any) => (
              <div key={sale.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{sale.raffle?.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{sale.customer?.firstName} {sale.customer?.lastName} · {sale.quantity} ticket{sale.quantity > 1 ? 's' : ''}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(sale.totalAmount, sale.raffle?.currency || currency)}</p>
                  <p className="text-xs text-emerald-600">+{formatCurrency(sale.commissionAmount, sale.raffle?.currency || currency)}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </AgentLayout>
  );
};
